import { Component } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';

import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';

import { jobCreateCustomerFeature } from 'src/app/job/job-create/job-create-customer/job-create-customer.feature';
import { EMAIL_PATTERN, PHONE_PATTERN } from 'src/app/job/job-helpers';
import { overviewLocationsFeature } from 'src/app/job/job-overview/overview-locations/overview-locations.feature';
import { selectJob } from 'src/app/job/job-tool.reducer';
import { selectAddEstimateBlockedReason } from 'src/app/job/job-workorders/job-workorders.selectors';
import { BuildingType, buildingTypesWithAccess } from 'src/app/shared/location-details-wizard/location-details-wizard.state';
import { createConfigSelector } from 'src/app/state/branding.store';
import { LoadingState } from 'src/app/utilities/state.util';

import { NewOpportunityActions } from '../new-opportunity.actions';
import { newOpportunityFeature } from '../new-opportunity.feature';
import { ControlMeta, NEW_OPPORTUNITY_STEP_TITLES, NEW_OPPORTUNITY_STEPS } from '../new-opportunity.state';

export interface ControlFieldStatus {
  error: string | null;
  dirty: boolean;
  touched: boolean;
}

// Validity is derived from store state so it survives the step unmounting;
// controlMeta only contributes presentation metadata (dirty, touched)
// while the control is mounted
function buildControlFieldStatus(
  keyErrorMap: Record<string, string | null>,
  controlMeta: Record<string, ControlMeta>,
): Record<string, ControlFieldStatus> {

  const status: Record<string, ControlFieldStatus> = {};

  for (const [ key, error ] of Object.entries(keyErrorMap)) {
    if (error) {

      const meta = controlMeta[key];

      status[key] = {
        error,
        dirty: meta?.dirty ?? false,
        touched: meta?.touched ?? false,
      };
    }
  }

  return status;
}

export const selectCustomerStepControlStatus = createSelector(
  jobCreateCustomerFeature.selectCustomerEditor,
  createConfigSelector('JOBS_HOWHEARDMANDATORY'),
  newOpportunityFeature.selectControlMeta,
  (editor, howHeardMandatory, controlMeta) => {

    const keyErrorMap: Record<string, string | null> = {
      fullName: !editor?.fullName ? 'Add full name' : null,
      referralSource: !editor?.referralSource && howHeardMandatory ? 'Add referral source' : null,
      email: editor?.email && !EMAIL_PATTERN.test(editor.email) ? 'Verify email format' : null,
      phone: editor?.phone && !PHONE_PATTERN.test(editor.phone) ? 'Verify phone format' : null,
    };

    return buildControlFieldStatus(keyErrorMap, controlMeta);
  },
);

export const selectWillGenerateEstimatePreview = createSelector(
  createConfigSelector('JOBS_GENERATEESTIMATEPREVIEW'),
  selectAddEstimateBlockedReason,
  (generateEstimatePreview, estimateBlockedReason) => Boolean(generateEstimatePreview) && !estimateBlockedReason,
);

export const selectLocationStepControlStatus = createSelector(
  overviewLocationsFeature.selectLocationEditors,
  createConfigSelector('JOBS_DWELLINGTYPEMANDATORY'),
  createConfigSelector('JOBS_ACCESSMANDATORY'),
  createConfigSelector('JOBS_BEDROOMSMANDATORY'),
  newOpportunityFeature.selectControlMeta,
  (editors, dwellingTypeMandatory, accessMandatory, bedroomsMandatory, controlMeta) => {

    const startBuildingTypeHasAccess = buildingTypesWithAccess.includes(editors?.start?.buildingType as BuildingType);
    const endBuildingTypeHasAccess = buildingTypesWithAccess.includes(editors?.end?.buildingType as BuildingType);

    const keyErrorMap: Record<string, string | null> = {
      startDwellingType: editors?.start?.selectedLocation && dwellingTypeMandatory && !editors.start.buildingType
        ? 'Add start dwelling type' : null,
      startBedrooms: editors?.start?.selectedLocation && bedroomsMandatory && !editors.start.size
        ? 'Add start bedrooms/size' : null,
      startAccess: editors?.start?.selectedLocation && accessMandatory && startBuildingTypeHasAccess && !editors.start.access
        ? 'Add start access' : null,
      endDwellingType: editors?.end?.selectedLocation && dwellingTypeMandatory && !editors.end.buildingType
        ? 'Add end dwelling type' : null,
      endBedrooms: editors?.end?.selectedLocation && bedroomsMandatory && !editors.end.size
        ? 'Add end bedrooms/size' : null,
      endAccess: editors?.end?.selectedLocation && accessMandatory && endBuildingTypeHasAccess && !editors.end.access
        ? 'Add end access' : null,
    };

    return buildControlFieldStatus(keyErrorMap, controlMeta);
  }
);

@Component({
    selector: 'app-step-navigation',
    standalone: true,
    imports: [
        FreyaCommonModule,
    ],
    templateUrl: './step-navigation.component.html',
    styleUrls: ['./step-navigation.component.scss']
})
export class StepNavigationComponent {

    public actions = NewOpportunityActions;

    viewModel$ = this.store.select(createSelector({
        nextButton: createSelector(
          selectWillGenerateEstimatePreview,
          newOpportunityFeature.selectStep,
          (generateEstimatePreview, step) => {
              const currentIndex = NEW_OPPORTUNITY_STEPS.indexOf(step);
              const isLastStep = currentIndex === NEW_OPPORTUNITY_STEPS.length - 1;

              const willGenerateEstimatePreview = isLastStep && generateEstimatePreview;

              const nextStep = NEW_OPPORTUNITY_STEPS[currentIndex + 1];
              const nextStepLabel = nextStep ? `Continue to ${NEW_OPPORTUNITY_STEP_TITLES[nextStep]}` : 'Next Step';

              return {
                  label: willGenerateEstimatePreview ? 'Generate Estimate Preview' : isLastStep ? 'Done' : nextStepLabel,
                  icon: isLastStep ? 'pi pi-check' : 'pi pi-arrow-right',
                  iconPos: isLastStep ? 'left' : 'right',
                  styleClass: 'p-button-success next-step-button',
              };
            },
        ),
        previousButton: createSelector(newOpportunityFeature.selectStep, (step) => {
            const currentIndex = NEW_OPPORTUNITY_STEPS.indexOf(step);
            if (currentIndex === 0) {
                return null;
            }
            return {
                label: 'Previous Step',
                icon: 'pi pi-arrow-left',
            };
        }),
        nextButtonDisabled: createSelector(
            newOpportunityFeature.selectStep,
            selectCustomerStepControlStatus,
            selectLocationStepControlStatus,
            selectJob,
            newOpportunityFeature.selectCreateJobCall,
            (step, customerStepValidationStatus, locationStepValidationStatus, job, createJobCall) => {
                // Requirement `#6401` - When a job is already loaded (e.g. a system-created lead being
                // completed with the customer on the phone), never block navigation:
                // the agent fills the remaining fields in whatever order they like.
                // The hard block is a create-flow guard only.
                // Checked first: createJobCall is never reset, so a stale MUTATING from an
                // abandoned create must not block an existing job.
                if (job) {
                  return false;
                }

                // The job is created in the background on entering the inventory step. Until it
                // has loaded there is no jobId in the route, so "Done" would navigate to
                // /jobs/undefined and fetch a job that does not exist.
                if (createJobCall === LoadingState.MUTATING || createJobCall === LoadingState.MUTATED) {
                  return true;
                }

                if (step === 'customer') {
                  return Object.values(customerStepValidationStatus).some(f => f.error !== null);
                }

                if (step === 'locations') {
                  return Object.values(locationStepValidationStatus).some(f => f.error !== null);
                }

                return false;
            },
        ),
        nextButtonErrors: createSelector(
            newOpportunityFeature.selectStep,
            selectCustomerStepControlStatus,
            selectLocationStepControlStatus,
            selectJob,
            (step, customerStepValidationStatus, locationStepValidationStatus, job): string[] => {
                if (job) return [];

                let status: Record<string, ControlFieldStatus> = {};

                if (step === 'customer') {
                    status = customerStepValidationStatus;
                } else if (step === 'locations') {
                    status = locationStepValidationStatus;
                }

                const requireTouched = step === 'customer';

                return Object.values(status)
                    .filter(f => f.error !== null && (!requireTouched || f.touched))
                    .map(f => f.error as string);
            },
        ),
        step: newOpportunityFeature.selectStep,
    }));

    constructor(
        public store: Store,
    ) {}
}
